/**
 * Seating groups and the registration report — wire contracts (plan
 * 2026-09-09-1309, U15 — R3, R31, R35; KTD12).
 *
 *   GET    /v1/ticketed-events/:eventPublicId/seating-groups
 *   POST   /v1/ticketed-events/:eventPublicId/seating-groups
 *   PATCH  /v1/ticketed-events/:eventPublicId/seating-groups/:groupPublicId
 *   DELETE /v1/ticketed-events/:eventPublicId/seating-groups/:groupPublicId
 *   POST   /v1/ticketed-events/:eventPublicId/seating-groups/assign
 *   GET    /v1/ticketed-events/:eventPublicId/admissions/report
 *
 * Naming (KTD1): the schemas say `admission` and `seating group`; an admin
 * reads "registration" and "table".
 */

import { z } from 'zod'
import {
  SeatingGroupCapacitySchema,
  TicketedEventSeatingGroupSchema
} from '../../schemas/entities/ticketed-event-seating-group'
import { MAX_ADMISSIONS_PER_VERB_CALL } from './admissions'

const SeatingGroupNameSchema = z.string().trim().min(1).max(255)

// ============================================================================
// Group CRUD
// ============================================================================

export const ListSeatingGroupsResponseSchema = z.object({
  data: z.array(TicketedEventSeatingGroupSchema)
})
export type ListSeatingGroupsResponse = z.infer<typeof ListSeatingGroupsResponseSchema>

/** A new table. `sortOrder` defaults to after the event's last table. */
export const CreateSeatingGroupRequestSchema = z
  .object({
    name: SeatingGroupNameSchema,
    capacity: SeatingGroupCapacitySchema.nullable().optional(),
    sortOrder: z.number().int().min(0).optional()
  })
  .strict()
export type CreateSeatingGroupRequest = z.infer<typeof CreateSeatingGroupRequestSchema>

/** Rename a table or change its capacity. `capacity: null` removes the limit. */
export const UpdateSeatingGroupRequestSchema = z
  .object({
    name: SeatingGroupNameSchema.optional(),
    capacity: SeatingGroupCapacitySchema.nullable().optional(),
    sortOrder: z.number().int().min(0).optional()
  })
  .strict()
  .refine(b => b.name !== undefined || b.capacity !== undefined || b.sortOrder !== undefined, {
    message: 'Provide at least one field to change'
  })
export type UpdateSeatingGroupRequest = z.infer<typeof UpdateSeatingGroupRequestSchema>

export const DeleteSeatingGroupResponseSchema = z.object({
  id: z.string(),
  /** Registrations that were at the table and now have none. Their status is unchanged. */
  unassignedCount: z.number().int().min(0)
})
export type DeleteSeatingGroupResponse = z.infer<typeof DeleteSeatingGroupResponseSchema>

// ============================================================================
// Assign
// ============================================================================

/** Put registrations at a table, or take them off any table with `seatingGroupId: null`. */
export const AssignSeatingGroupRequestSchema = z
  .object({
    admissionPublicIds: z.array(z.string().min(1)).min(1).max(MAX_ADMISSIONS_PER_VERB_CALL),
    /** The table's public id; null removes the registrations from their table. */
    seatingGroupId: z.string().min(1).nullable()
  })
  .strict()
export type AssignSeatingGroupRequest = z.infer<typeof AssignSeatingGroupRequestSchema>

/**
 * `assigned` — moved to the table (or off it, for a null table);
 * `already_in_state` — already there; `invalid_transition` — a voided
 * registration, which is never seated; `not_found` — not on this event.
 */
export const AssignSeatingGroupOutcomeKindSchema = z.enum([
  'assigned',
  'already_in_state',
  'invalid_transition',
  'not_found'
])
export type AssignSeatingGroupOutcomeKind = z.infer<typeof AssignSeatingGroupOutcomeKindSchema>

export const AssignSeatingGroupResponseSchema = z.object({
  /** One per distinct requested id, in request order. */
  outcomes: z.array(z.object({ admissionPublicId: z.string(), outcome: AssignSeatingGroupOutcomeKindSchema })),
  /** The table after the call; null when the registrations were taken off their table. */
  group: TicketedEventSeatingGroupSchema.nullable(),
  /** How many live registrations the table now holds past its capacity. Going over is allowed, never refused. */
  overCapacityCount: z.number().int().min(0)
})
export type AssignSeatingGroupResponse = z.infer<typeof AssignSeatingGroupResponseSchema>

// ============================================================================
// Report
// ============================================================================

/**
 * Every count the registration list header shows (R31). The states never
 * overlap where it matters:
 *   - `registered` is active plus deactivated; a voided registration is in
 *     `voided` and nowhere else (AE2);
 *   - `checkedIn` and `unassigned` count registered ones only;
 *   - `noShow` is active and not checked in, and null until the event has
 *     ended;
 *   - `overCapacity` is registered ones an admin carried past a ticket type's
 *     capacity.
 * `unassigned` (no holder name yet) is a different number from unsold
 * capacity (R3, AE11).
 */
export const AdmissionReportCountsSchema = z.object({
  registered: z.number().int().min(0),
  cancelled: z.number().int().min(0),
  voided: z.number().int().min(0),
  deactivated: z.number().int().min(0),
  checkedIn: z.number().int().min(0),
  noShow: z.number().int().min(0).nullable(),
  unassigned: z.number().int().min(0),
  overCapacity: z.number().int().min(0)
})
export type AdmissionReportCounts = z.infer<typeof AdmissionReportCountsSchema>

export const AdmissionReportResponseSchema = z.object({
  totals: AdmissionReportCountsSchema,
  /** One entry per ticket type on the event, in the event's ticket-type order, including empty ones. */
  byTicketType: z.array(
    AdmissionReportCountsSchema.extend({
      ticketTypeId: z.string(),
      ticketTypeName: z.string()
    })
  ),
  /** When no-shows start counting: the event's start plus its duration. */
  noShowFrom: z.string()
})
export type AdmissionReportResponse = z.infer<typeof AdmissionReportResponseSchema>
